import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import TreeNavigator from "@/components/TreeNavigator";
import { ArrowRight, Lock, Lightbulb, Search } from "lucide-react";

export default function ResearchIdeasPublic() {
  const { user } = useAuth();
  const [nodes, setNodes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selected) params.taxonomy_id = selected.id;
      if (search) params.q = search;
      const r = await api.get("/research-ideas", { params });
      setIdeas(r.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/taxonomy", { params: { domain: "research_ideas" } }).then((r) => setNodes(r.data || []));
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [selected, search]);

  return (
    <div className="min-h-screen bg-kb-bg" data-testid="research-ideas-public-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PREMIUM CATALOGUE</div>
        <h1 className="font-serif text-4xl lg:text-6xl text-kb-ink mt-2 leading-tight">
          The <em className="italic text-kb-primary">Research Idea Center</em>.
        </h1>
        <p className="mt-5 text-kb-ink-muted max-w-2xl">
          Hand-curated research ideas across CS, Health and Climate domains. Each idea contains a
          gap statement, suggested methodology, literature review, and summary brief — organised by
          Domain → Subdomain → Topic → Research Area.
        </p>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-4">
            <div className="kb-card p-5 sticky top-20">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">DOMAIN HIERARCHY</div>
              <div className="mt-3 max-h-[60vh] overflow-y-auto pr-2">
                {nodes.length === 0 ? (
                  <div className="kb-mono text-xs text-kb-ink-muted py-4">Loading hierarchy…</div>
                ) : (
                  <TreeNavigator nodes={nodes} selectedId={selected?.id} onSelect={setSelected} testIdPrefix="ideas-tree" />
                )}
              </div>
            </div>
          </aside>
          <div className="lg:col-span-8">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-kb-ink-muted" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search ideas by title, keywords, or tag…"
                className="kb-input text-sm pl-9 w-full"
                data-testid="research-ideas-search-input"
              />
            </div>

            {selected && (
              <div className="mt-4 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                FILTERED BY <span className="text-kb-primary">{(selected.path || []).join(" › ")}</span>
              </div>
            )}

            {loading ? (
              <div className="mt-10 kb-mono text-xs text-kb-ink-muted">Loading ideas…</div>
            ) : ideas.length === 0 ? (
              <div className="mt-12 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">
                <Lightbulb className="w-8 h-8 text-kb-ink-light mx-auto mb-3" strokeWidth={1.2} />
                No ideas match this filter yet. Try a different domain or remove the search.
              </div>
            ) : (
              <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="research-ideas-list">
                {ideas.map((i) => (
                  <Link
                    to={`/research-ideas/${i.id}`}
                    key={i.id}
                    className="kb-card p-5 hover:shadow-md transition-shadow"
                    data-testid={`idea-card-${i.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary line-clamp-1">
                        {(i.path || []).join(" › ")}
                      </div>
                      {i.locked && <Lock className="w-3.5 h-3.5 text-kb-ink-muted shrink-0" />}
                    </div>
                    <h3 className="font-serif text-lg text-kb-ink mt-2 leading-snug">{i.title}</h3>
                    {i.overview && <p className="text-sm text-kb-ink-muted mt-2 line-clamp-3">{i.overview}</p>}
                    <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between">
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                        {(i.relevant_papers?.length || 0)} REF · {i.difficulty?.toUpperCase()}
                      </div>
                      <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1">
                        {i.locked ? "Unlock" : "Read"} <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {!user && (
              <div className="mt-10 kb-card p-6 text-center">
                <h3 className="font-serif text-xl text-kb-ink">Get full access</h3>
                <p className="text-sm text-kb-ink-muted mt-2">Unlock literature reviews, methodology guides, and PDF briefs.</p>
                <Link to="/pricing" className="kb-btn-primary mt-4 inline-flex" data-testid="ideas-pricing-cta">
                  View plans <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
